import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Card,
  Modal,
  Select,
  message,
} from "antd";
import { EditOutlined, SwapOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../../Contexts/user";
import {
  getContracts,
  updateContractStatus as updateStatus,
  generateContractPdf,
} from "./services/contractApi";
import "./styles.css";

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const STATUS_OPTIONS = [
  { value: "vendor", label: "Vendor", color: "blue" },
  { value: "merchant", label: "Merchant", color: "purple" },
  { value: "ap", label: "AP", color: "cyan" },
  { value: "dp", label: "DP", color: "orange" },
  { value: "completed", label: "Completed", color: "green" },
];

const Contract = () => {
  const navigate = useNavigate();
  const { userData } = useUserContext();

  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  // Change status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusRecord, setStatusRecord] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContracts();
      const keyed = data.map((c, i) => ({ ...c, key: c.id || i }));
      setContracts(keyed);
      setFilteredContracts(keyed);
    } catch {
      message.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // ---- Search ----

  useEffect(() => {
    if (!searchText) {
      setFilteredContracts(contracts);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredContracts(
      contracts.filter(
        (c) =>
          c.exhibitorNo?.toLowerCase().includes(lower) ||
          c.exhibitorName?.toLowerCase().includes(lower) ||
          c.merchantName?.toLowerCase().includes(lower)
      )
    );
  }, [searchText, contracts]);

  // ---- Status ----

  const getStatusTag = (status) => {
    const found = STATUS_OPTIONS.find((s) => s.value === status);
    if (found) return <Tag color={found.color}>{found.label}</Tag>;
    return <Tag color="default">{status}</Tag>;
  };

  const openStatusModal = (record) => {
    setStatusRecord(record);
    setNewStatus(record.status);
    setStatusModalOpen(true);
  };

  const handleStatusChange = async () => {
    setStatusLoading(true);
    try {
      await updateStatus(statusRecord.key, newStatus);
      message.success("Status updated successfully");
      setStatusModalOpen(false);
      fetchContracts();
    } catch {
      message.error("Failed to update status");
    } finally {
      setStatusLoading(false);
    }
  };

  // ---- PDF Download ----

  const handleDownloadPdf = async (record) => {
    setPdfLoadingId(record.key);
    try {
      const blob = await generateContractPdf(record.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${record.exhibitorNo || record.key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to generate PDF");
    } finally {
      setPdfLoadingId(null);
    }
  };

  // ---- Edit ----

  const handleEdit = (record) => {
    navigate(`/admin/contracts/${record.key}/edit`);
  };

  // ---- Table columns ----

  const columns = [
    {
      title: "Exhibitor #",
      dataIndex: "exhibitorNo",
      sorter: (a, b) => (a.exhibitorNo || "").localeCompare(b.exhibitorNo || ""),
    },
    {
      title: "Exhibitor Name",
      dataIndex: "exhibitorName",
      sorter: (a, b) => (a.exhibitorName || "").localeCompare(b.exhibitorName || ""),
    },
    {
      title: "Merchant Name",
      dataIndex: "merchantName",
      sorter: (a, b) => (a.merchantName || "").localeCompare(b.merchantName || ""),
    },
    {
      title: "Form Status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
      filters: STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Form Updated Date",
      dataIndex: "updated",
    },
    {
      title: "Action",
      width: 260,
      render: (_, record) => (
        <Space wrap>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<SwapOutlined />}
            size="small"
            onClick={() => openStatusModal(record)}
          >
            Status
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            size="small"
            loading={pdfLoadingId === record.key}
            onClick={() => handleDownloadPdf(record)}
          >
            PDF
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="contract-container">
      <Card className="contract-card">
        {/* Top controls */}
        <Row
          justify="space-between"
          align="middle"
          className="top-controls"
          gutter={[16, 16]}
          wrap
        >
          <Col>
            <Space size="large">
              <div>
                <Text strong>Show: </Text>
                <Text>2026</Text>
              </div>
              <div>
                <Text strong>Exhibitor Option: </Text>
                <Text>Open House</Text>
              </div>
              <div>
                <Text strong>Team: </Text>
                <Text className="team-name">
                  {userData?.role?.charAt(0).toUpperCase() + userData?.role?.slice(1) || "—"}
                </Text>
              </div>
            </Space>
          </Col>
        </Row>

        {/* Search */}
        <Row
          justify="space-between"
          align="middle"
          className="table-controls"
          gutter={[16, 16]}
          wrap
        >
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-box"
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: "right" }}>
            <Text type="secondary">
              {filteredContracts.length} contract{filteredContracts.length !== 1 ? "s" : ""}
            </Text>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredContracts}
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} contracts`,
          }}
          className="contract-table"
        />
      </Card>

      {/* ========== CHANGE STATUS MODAL ========== */}
      <Modal
        title={`Change Status — ${statusRecord?.exhibitorName || ""}`}
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={handleStatusChange}
        confirmLoading={statusLoading}
        okText="Update"
        destroyOnClose
      >
        <div className="status-modal-content">
          <Text>Current status: {statusRecord && getStatusTag(statusRecord.status)}</Text>
          <div className="status-select-wrapper">
            <Text strong>New Status:</Text>
            <Select
              value={newStatus}
              onChange={setNewStatus}
              className="status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Contract;
